import type { Area, Device, Entity, Floor, RegistryState } from './types';

export interface RegistryMaps {
  floorById: Map<string, Floor>;
  areaById: Map<string, Area>;
  deviceById: Map<string, Device>;
  entityById: Map<string, Entity>;
  entitiesByArea: Map<string, Entity[]>;
  entitiesByFloor: Map<string, Entity[]>;
  entitiesByDevice: Map<string, Entity[]>;
}

export function buildRegistryMaps(state: RegistryState): RegistryMaps {
  const floorById = new Map<string, Floor>();
  for (const floor of state.floors) {
    floorById.set(floor.floor_id, floor);
  }

  const areaById = new Map<string, Area>();
  for (const area of state.areas) {
    areaById.set(area.area_id, area);
  }

  const deviceById = new Map<string, Device>();
  for (const device of state.devices) {
    deviceById.set(device.device_id, device);
  }

  const entityById = new Map<string, Entity>();
  const entitiesByArea = new Map<string, Entity[]>();
  const entitiesByFloor = new Map<string, Entity[]>();
  const entitiesByDevice = new Map<string, Entity[]>();

  for (const entity of state.entities) {
    entityById.set(entity.entity_id, entity);

    const areaId =
      entity.area_id ??
      (entity.device_id ? deviceById.get(entity.device_id)?.area_id : undefined);

    if (areaId) {
      const list = entitiesByArea.get(areaId) ?? [];
      list.push(entity);
      entitiesByArea.set(areaId, list);

      const area = areaById.get(areaId);
      if (area) {
        const floorList = entitiesByFloor.get(area.floor_id) ?? [];
        floorList.push(entity);
        entitiesByFloor.set(area.floor_id, floorList);
      }
    }

    if (entity.device_id) {
      const list = entitiesByDevice.get(entity.device_id) ?? [];
      list.push(entity);
      entitiesByDevice.set(entity.device_id, list);
    }
  }

  return {
    floorById,
    areaById,
    deviceById,
    entityById,
    entitiesByArea,
    entitiesByFloor,
    entitiesByDevice,
  };
}

export function sortFloors(floors: Floor[]): Floor[] {
  return [...floors].sort((a, b) => a.level - b.level);
}

const CUSTOMER_HIDDEN_DOMAINS = new Set(['update', 'button', 'event', 'automation']);
const CUSTOMER_HIDDEN_CATEGORIES = new Set(['diagnostic', 'config']);

export function isCustomerHidden(entity: Entity): boolean {
  const domain = entity.entity_id.split('.', 1)[0];
  if (CUSTOMER_HIDDEN_DOMAINS.has(domain)) return true;
  // Hide dead/unknown entities from customer view
  if (entity.state === 'unavailable' || entity.state === 'unknown') return true;
  const attrs = entity.attributes as { hidden?: unknown; entity_category?: unknown } | undefined;
  if (attrs?.hidden === true) return true;
  if (typeof attrs?.entity_category === 'string' && CUSTOMER_HIDDEN_CATEGORIES.has(attrs.entity_category)) {
    return true;
  }
  // Hide diagnostic Shelly-Sensors (RSSI, uptime, etc.) that carry no customer value
  const dc = typeof (attrs as Record<string, unknown> | undefined)?.device_class === 'string' ? (attrs as Record<string, unknown>).device_class : '';
  if (dc === 'signal_strength' || dc === 'duration' || dc === 'timestamp') return true;
  return false;
}

export function filterVisibleEntities(entities: Entity[], hiddenLabels: string[]): Entity[] {
  const hiddenSet = new Set(hiddenLabels);
  return entities.filter((entity) => {
    if (isCustomerHidden(entity) && !(entity.labels || []).includes("rsc-fav")) return false;
    if (hiddenLabels.length > 0 && entity.labels.some((label) => hiddenSet.has(label))) return false;
    return true;
  });
}

export function getEntityPriority(entity: Entity, pinnedLabels: string[], heroLabels: string[] = []): number {
  const pinnedSet = new Set(pinnedLabels);
  if (entity.labels.some((label) => pinnedSet.has(label))) return 2;
  const heroSet = new Set(heroLabels);
  if (entity.labels.some((label) => heroSet.has(label))) return 1;
  return 0;
}

export function isCompactEntity(entity: Entity, compactLabels: string[]): boolean {
  if (compactLabels.length === 0) return false;
  const compactSet = new Set(compactLabels);
  return entity.labels.some((label) => compactSet.has(label));
}
